import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, type OrganizationEntitlements } from '../../api/client';
import DataTable, { Column } from '../../components/DataTable';
import Button from '../../components/ui/Button';
import { FormField, ConfirmModal } from '../../components/ui';
import NumericInput from '../../components/NumericInput';
import Modal from '../../components/Modal';
import ToggleSwitch from '../../components/forms/ToggleSwitch';
import '../../App.css';
import '../admin/BillingPage.css';
import './OrganizationBillingTab.css';
import { translateError } from '../../utils/errorMessages';

interface IOrganization {
  _id: string;
  name: string;
  slug: string;
  contactEmail: string;
  // plan/status live INSIDE subscription (the backend serializes
  // `subscription: org.subscription`), not as flat fields.
  subscription?: {
    plan?: string;
    status?: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
    stripeCustomerId?: string;
    stripeSubscriptionId?: string;
    expiresAt?: string;
    entitlements?: Partial<OrganizationEntitlements>;
  };
  settings?: {
    defaultLanguage: string;
    allowSelfRegistration: boolean;
  };
  createdAt: string;
}

interface IUser {
  _id: string;
  name: string;
  email: string;
  role?: {
    name: string;
  };
  lastLogin?: string;
}

interface IBillingPreview {
  periodStart: string | null;
  periodEnd: string | null;
  lastSampledAt: string | null;
  current: { members: number; activities: number };
  peak: { members: number; activities: number };
  currency: string;
  netAmount: number;
  vatRate: number;
  vatAmount: number;
  grossAmount: number;
  billable: boolean;
  exempt: boolean;
  hasBillingDetails: boolean;
}

interface IInvoice {
  _id: string;
  invoiceNumber: string | null;
  periodStart: string;
  periodEnd: string;
  netAmount: number;
  vatAmount: number;
  grossAmount: number;
  currency: string;
  status: 'draft' | 'issued' | 'paid' | 'overdue' | 'cancelled';
  issuedAt: string | null;
  dueDate: string | null;
  paidAt: string | null;
  emailSentAt: string | null;
}

/** One ended month and its single live record (null = none yet). */
interface IBillingPeriod {
  periodStart: string;
  periodEnd: string;
  record: (IInvoice & { peakMembers: number; peakActivities: number }) | null;
  recordedPeak: { members: number; activities: number } | null;
}

interface IBillingPeriods {
  periods: IBillingPeriod[];
  exempt: boolean;
  hasBillingDetails: boolean;
  missingBillingFields: string[];
  current: { members: number; activities: number };
}

/** The usage modal either creates a month's record or edits a draft's peaks. */
interface IUsageEdit {
  period: IBillingPeriod;
  members: string;
  activities: string;
}

const OrganizationDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState<IOrganization | null>(null);
  const [users, setUsers] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'users' | 'subscription' | 'billing'>('details');

  // Form state
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [status, setStatus] = useState('active');
  const [allowSelfRegistration, setAllowSelfRegistration] = useState(true);
  const [defaultLanguage, setDefaultLanguage] = useState('en');

  // Entitlements editor state (the org's spend ceiling). Stored as strings so
  // the inputs can be cleared without sticking at the minimum.
  const [memberLimit, setMemberLimit] = useState('100');
  const [activityLimit, setActivityLimit] = useState('3');
  const [storageMB, setStorageMB] = useState('0');
  const [savingEntitlements, setSavingEntitlements] = useState(false);

  // Billing tab state (lazy-loaded when the tab is first opened).
  const [billingPreview, setBillingPreview] = useState<IBillingPreview | null>(null);
  const [billingPeriods, setBillingPeriods] = useState<IBillingPeriods | null>(null);
  const [invoices, setInvoices] = useState<IInvoice[]>([]);
  const [billingLoaded, setBillingLoaded] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);

  // Billing-details form state
  const [legalName, setLegalName] = useState('');
  const [ico, setIco] = useState('');
  const [dic, setDic] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [note, setNote] = useState('');
  const [savingBillingDetails, setSavingBillingDetails] = useState(false);
  const [billingDetailsSaved, setBillingDetailsSaved] = useState(false);

  // Invoice actions
  const [usageEdit, setUsageEdit] = useState<IUsageEdit | null>(null);
  const [issueTarget, setIssueTarget] = useState<IBillingPeriod | null>(null);
  const [issueEmail, setIssueEmail] = useState(true);
  const [savingExempt, setSavingExempt] = useState(false);
  const [markPaidTarget, setMarkPaidTarget] = useState<IInvoice | null>(null);
  const [cancelTarget, setCancelTarget] = useState<IInvoice | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const [orgResponse, usersResponse] = await Promise.all([
        api.superAdmin.organizations.get(id),
        api.superAdmin.organizations.getUsers(id),
      ]);

      const orgData = orgResponse.data;
      setOrganization(orgData);
      setUsers(usersResponse.data);

      // Populate form
      setName(orgData.name);
      setSlug(orgData.slug);
      setContactEmail(orgData.contactEmail || '');
      setStatus(orgData.subscription?.status || 'active');
      setAllowSelfRegistration(orgData.settings?.allowSelfRegistration ?? true);
      setDefaultLanguage(orgData.settings?.defaultLanguage || 'en');

      const ent = orgData.subscription?.entitlements;
      setMemberLimit(String(ent?.memberLimit ?? 100));
      setActivityLimit(String(ent?.activityLimit ?? 3));
      setStorageMB(String(ent?.storageMB ?? 0));

      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
      if (err.response?.status === 404) {
        navigate('/super-admin/organizations');
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const submitHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      await api.superAdmin.organizations.update(id!, {
        name,
        slug,
        contactEmail,
        status,
        settings: {
          allowSelfRegistration,
          defaultLanguage,
        },
      });
      navigate('/super-admin/organizations');
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setSaving(false);
    }
  };

  const saveEntitlements = async () => {
    setSavingEntitlements(true);
    setError(null);
    try {
      await api.superAdmin.organizations.update(id!, {
        subscription: {
          entitlements: {
            memberLimit: Math.max(100, Math.trunc(Number(memberLimit) || 0)),
            activityLimit: Math.max(3, Math.trunc(Number(activityLimit) || 0)),
            storageMB: Math.max(0, Math.trunc(Number(storageMB) || 0)),
          },
        },
      });
      await fetchData();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setSavingEntitlements(false);
    }
  };

  // Every invoice action changes the periods table too, so refresh both.
  const fetchInvoices = useCallback(async () => {
    if (!id) return;
    const [invoicesRes, periodsRes, previewRes] = await Promise.all([
      api.superAdmin.organizations.getInvoices(id),
      api.superAdmin.organizations.getBillingPeriods(id),
      api.superAdmin.organizations.getBillingPreview(id),
    ]);
    setInvoices(invoicesRes.data.invoices || []);
    setBillingPeriods(periodsRes.data);
    setBillingPreview(previewRes.data);
  }, [id]);

  const fetchBilling = useCallback(async () => {
    if (!id) return;
    setBillingLoading(true);
    setBillingError(null);
    try {
      const [previewRes, detailsRes, invoicesRes, periodsRes] = await Promise.all([
        api.superAdmin.organizations.getBillingPreview(id),
        api.superAdmin.organizations.getBillingDetails(id),
        api.superAdmin.organizations.getInvoices(id),
        api.superAdmin.organizations.getBillingPeriods(id),
      ]);

      setBillingPreview(previewRes.data);
      setBillingPeriods(periodsRes.data);

      const bd = detailsRes.data.billingDetails;
      setLegalName(bd?.legalName || '');
      setIco(bd?.ico || '');
      setDic(bd?.dic || '');
      setStreet(bd?.address?.street || '');
      setCity(bd?.address?.city || '');
      setZip(bd?.address?.zip || '');
      setCountry(bd?.address?.country || '');
      setBillingEmail(bd?.billingEmail || '');
      setNote(bd?.note || '');

      setInvoices(invoicesRes.data.invoices || []);
      setBillingLoaded(true);
    } catch (err: any) {
      setBillingError(translateError(err, t));
    } finally {
      setBillingLoading(false);
    }
  }, [id]);

  // Lazy-load billing data the first time the tab is opened.
  useEffect(() => {
    if (activeTab === 'billing' && !billingLoaded && !billingLoading) {
      fetchBilling();
    }
  }, [activeTab, billingLoaded, billingLoading, fetchBilling]);

  const saveBillingDetails = async () => {
    setSavingBillingDetails(true);
    setBillingError(null);
    setBillingDetailsSaved(false);
    try {
      await api.superAdmin.organizations.updateBillingDetails(id!, {
        legalName,
        ico: ico || undefined,
        dic: dic || undefined,
        address: { street, city, zip, country },
        billingEmail: billingEmail || undefined,
        note: note || undefined,
      });
      setBillingDetailsSaved(true);
      await fetchInvoices();
    } catch (err: any) {
      setBillingError(translateError(err, t));
    } finally {
      setSavingBillingDetails(false);
    }
  };

  const openUsageEdit = (period: IBillingPeriod) => {
    const seed = period.record
      ? { members: period.record.peakMembers, activities: period.record.peakActivities }
      : period.recordedPeak ?? billingPeriods?.current ?? { members: 0, activities: 0 };
    setUsageEdit({ period, members: String(seed.members), activities: String(seed.activities) });
  };

  const saveUsageEdit = async () => {
    if (!usageEdit) return;
    setActionLoading(true);
    setBillingError(null);
    const peakMembers = Math.max(0, Math.trunc(Number(usageEdit.members) || 0));
    const peakActivities = Math.max(0, Math.trunc(Number(usageEdit.activities) || 0));
    try {
      if (usageEdit.period.record) {
        await api.superAdmin.invoices.updatePeaks(usageEdit.period.record._id, { peakMembers, peakActivities });
      } else {
        await api.superAdmin.organizations.createBillingPeriod(id!, {
          periodStart: usageEdit.period.periodStart,
          peakMembers,
          peakActivities,
        });
      }
      setUsageEdit(null);
      await fetchInvoices();
    } catch (err: any) {
      setBillingError(translateError(err, t));
    } finally {
      setActionLoading(false);
    }
  };

  const confirmIssue = async () => {
    if (!issueTarget?.record) return;
    setActionLoading(true);
    setBillingError(null);
    try {
      await api.superAdmin.invoices.issue(issueTarget.record._id, issueEmail);
      setIssueTarget(null);
      await fetchInvoices();
    } catch (err: any) {
      setBillingError(translateError(err, t));
    } finally {
      setActionLoading(false);
    }
  };

  const toggleExempt = async (exempt: boolean) => {
    setSavingExempt(true);
    setBillingError(null);
    try {
      await api.superAdmin.organizations.setBillingExempt(id!, exempt);
      await fetchInvoices();
    } catch (err: any) {
      setBillingError(translateError(err, t));
    } finally {
      setSavingExempt(false);
    }
  };

  const handleDownloadPdf = async (invoice: IInvoice) => {
    setBillingError(null);
    try {
      const response = await api.superAdmin.invoices.downloadPdf(invoice._id);
      // response.data is already a Blob when responseType is 'blob'
      const blob = response.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setBillingError(translateError(err, t));
    }
  };

  const handleResendEmail = async (invoice: IInvoice) => {
    setBillingError(null);
    try {
      await api.superAdmin.invoices.resendEmail(invoice._id);
      await fetchInvoices();
    } catch (err: any) {
      setBillingError(translateError(err, t));
    }
  };

  const confirmMarkPaid = async () => {
    if (!markPaidTarget) return;
    setActionLoading(true);
    setBillingError(null);
    try {
      await api.superAdmin.invoices.markPaid(markPaidTarget._id);
      setMarkPaidTarget(null);
      await fetchInvoices();
    } catch (err: any) {
      setBillingError(translateError(err, t));
    } finally {
      setActionLoading(false);
    }
  };

  const confirmCancelInvoice = async () => {
    if (!cancelTarget) return;
    setActionLoading(true);
    setBillingError(null);
    try {
      await api.superAdmin.invoices.cancel(cancelTarget._id);
      setCancelTarget(null);
      await fetchInvoices();
    } catch (err: any) {
      setBillingError(translateError(err, t));
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString() : '-';

  const formatMonth = (value: string) =>
    new Date(`${value.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  const formatMoney = (amount: number, currency: string) => `${Number(amount).toFixed(2)} ${currency}`;

  const statusLabelKey: Record<IInvoice['status'], string> = {
    draft: 'superAdmin.billing.statusDraft',
    issued: 'superAdmin.billing.statusIssued',
    paid: 'superAdmin.billing.statusPaid',
    overdue: 'superAdmin.billing.statusOverdue',
    cancelled: 'superAdmin.billing.statusCancelled',
  };

  const invoiceColumns: Column<IInvoice>[] = useMemo(() => [
    {
      header: t('superAdmin.billing.invoiceNumber'),
      render: (inv) => inv.invoiceNumber ?? '–',
    },
    {
      header: t('superAdmin.billing.period'),
      render: (inv) => (
        <span className="invoice-period">
          {formatDate(inv.periodStart)} – {formatDate(inv.periodEnd)}
        </span>
      ),
    },
    {
      header: t('superAdmin.billing.gross'),
      render: (inv) => (
        <span className="invoice-amount">
          {Number(inv.grossAmount).toFixed(2)} {inv.currency}
        </span>
      ),
    },
    {
      header: t('superAdmin.billing.status'),
      render: (inv) => (
        <span className={`badge badge-status-${inv.status}`}>
          {t(statusLabelKey[inv.status])}
        </span>
      ),
    },
    {
      header: t('superAdmin.billing.issued'),
      render: (inv) => formatDate(inv.issuedAt),
    },
    {
      header: t('superAdmin.billing.due'),
      render: (inv) => formatDate(inv.dueDate),
    },
    {
      header: t('superAdmin.billing.actions'),
      // Icon-only: the labels ride along as title + aria-label, so the column
      // stops dictating the table's width and rows stay one line tall.
      render: (inv) => (
        <div className="invoice-actions">
          {inv.invoiceNumber && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleDownloadPdf(inv)}
              title={t('superAdmin.billing.downloadPdf')}
              aria-label={t('superAdmin.billing.downloadPdf')}
            >
              <i className="fas fa-download" aria-hidden="true" />
            </Button>
          )}
          {(inv.status === 'issued' || inv.status === 'overdue') && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setMarkPaidTarget(inv)}
              title={t('superAdmin.billing.markPaid')}
              aria-label={t('superAdmin.billing.markPaid')}
            >
              <i className="fas fa-check" aria-hidden="true" />
            </Button>
          )}
          {inv.status !== 'cancelled' && inv.status !== 'draft' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleResendEmail(inv)}
              title={t('superAdmin.billing.resendEmail')}
              aria-label={t('superAdmin.billing.resendEmail')}
            >
              <i className="fas fa-envelope" aria-hidden="true" />
            </Button>
          )}
          {inv.status !== 'cancelled' && inv.status !== 'paid' && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => setCancelTarget(inv)}
              title={t('superAdmin.billing.cancel')}
              aria-label={t('superAdmin.billing.cancel')}
            >
              <i className="fas fa-ban" aria-hidden="true" />
            </Button>
          )}
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // Complimentary only stops automatic billing; manual invoicing stays available.
  const canIssue = !!billingPeriods?.hasBillingDetails;

  const periodColumns: Column<IBillingPeriod>[] = useMemo(() => [
    {
      header: t('superAdmin.billing.month'),
      render: (p) => <span className="invoice-period">{formatMonth(p.periodStart)}</span>,
    },
    {
      header: t('superAdmin.billing.usage'),
      render: (p) =>
        p.record
          ? t('superAdmin.billing.usageValue', { members: p.record.peakMembers, activities: p.record.peakActivities })
          : '–',
    },
    {
      header: t('superAdmin.billing.gross'),
      render: (p) => (
        <span className="invoice-amount">
          {p.record ? formatMoney(p.record.grossAmount, p.record.currency) : '–'}
        </span>
      ),
    },
    {
      header: t('superAdmin.billing.status'),
      render: (p) =>
        p.record ? (
          <span className={`badge badge-status-${p.record.status}`}>{t(statusLabelKey[p.record.status])}</span>
        ) : (
          <span className="billing-section-note">{t('superAdmin.billing.noRecord')}</span>
        ),
    },
    {
      header: t('superAdmin.billing.invoiceNumber'),
      render: (p) => p.record?.invoiceNumber ?? '–',
    },
    {
      header: t('superAdmin.billing.actions'),
      render: (p) => (
        <div className="invoice-actions">
          {!p.record && (
            <Button variant="secondary" size="sm" onClick={() => openUsageEdit(p)}>
              {t('superAdmin.billing.createRecord')}
            </Button>
          )}
          {p.record?.status === 'draft' && (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => openUsageEdit(p)}
                title={t('superAdmin.billing.editUsage')}
                aria-label={t('superAdmin.billing.editUsage')}
              >
                <i className="fas fa-pen" aria-hidden="true" />
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setIssueEmail(true);
                  setIssueTarget(p);
                }}
                disabled={!canIssue}
                title={canIssue ? t('superAdmin.billing.issue') : t('superAdmin.billing.issueNeedsDetails', { fields: billingPeriods?.missingBillingFields.join(', ') ?? '' })}
              >
                {t('superAdmin.billing.issue')}
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => setCancelTarget(p.record)}
                title={t('superAdmin.billing.discardDraft')}
                aria-label={t('superAdmin.billing.discardDraft')}
              >
                <i className="fas fa-trash" aria-hidden="true" />
              </Button>
            </>
          )}
          {p.record && p.record.status !== 'draft' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleDownloadPdf(p.record!)}
              title={t('superAdmin.billing.downloadPdf')}
              aria-label={t('superAdmin.billing.downloadPdf')}
            >
              <i className="fas fa-download" aria-hidden="true" />
            </Button>
          )}
          {(p.record?.status === 'issued' || p.record?.status === 'overdue') && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setMarkPaidTarget(p.record)}
              title={t('superAdmin.billing.markPaid')}
              aria-label={t('superAdmin.billing.markPaid')}
            >
              <i className="fas fa-check" aria-hidden="true" />
            </Button>
          )}
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, billingPeriods]);

  const userColumns: Column<IUser>[] = useMemo(() => [
    {
      header: t('common.name'),
      key: 'name',
    },
    {
      header: t('common.email'),
      key: 'email',
    },
    {
      header: t('superAdmin.users.role'),
      render: (user) => user.role?.name || '-',
    },
    {
      header: t('superAdmin.users.lastLogin'),
      render: (user) => user.lastLogin ? new Date(user.lastLogin).toLocaleString() : '-',
    },
  ], [t]);

  if (loading) return <p>{t('common.loading')}</p>;
  if (error && !organization) return <p className="error-message">{t('common.error')}: {error}</p>;
  if (!organization) return <p>{t('superAdmin.organizations.notFound')}</p>;

  return (
    <div className={`admin-form-page${activeTab === 'billing' ? ' admin-form-page--wide' : ''}`}>
      <h2>{t('superAdmin.organizations.editTitle')}</h2>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'details' ? 'active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          {t('superAdmin.organizations.tabDetails')}
        </button>
        <button
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          {t('superAdmin.organizations.tabUsers')} ({users.length})
        </button>
        <button
          className={`tab ${activeTab === 'subscription' ? 'active' : ''}`}
          onClick={() => setActiveTab('subscription')}
        >
          {t('superAdmin.organizations.tabSubscription')}
        </button>
        <button
          className={`tab ${activeTab === 'billing' ? 'active' : ''}`}
          onClick={() => setActiveTab('billing')}
        >
          {t('superAdmin.billing.tab')}
        </button>
      </div>

      {error && <p className="error-message">{t('common.error')}: {error}</p>}

      {activeTab === 'details' && (
        <form onSubmit={submitHandler} className="form-container">
          <div className="form-group">
            <label htmlFor="name">{t('common.name')}</label>
            <input
              type="text"
              id="name"
              placeholder={t('superAdmin.organizations.enterName')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="slug">{t('superAdmin.organizations.slug')}</label>
            <input
              type="text"
              id="slug"
              placeholder={t('superAdmin.organizations.enterSlug')}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              required
            />
            <small className="form-help">{t('superAdmin.organizations.slugHelp')}</small>
          </div>

          <div className="form-group">
            <label htmlFor="contactEmail">{t('superAdmin.organizations.contactEmail')}</label>
            <input
              type="email"
              id="contactEmail"
              placeholder={t('superAdmin.organizations.enterContactEmail')}
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="status">{t('common.status')}</label>
            <select
              id="status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="active">{t('superAdmin.status.active')}</option>
              <option value="expired">{t('superAdmin.status.expired')}</option>
              <option value="suspended">{t('superAdmin.status.suspended')}</option>
              <option value="cancelled">{t('superAdmin.status.cancelled')}</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="defaultLanguage">{t('superAdmin.organizations.defaultLanguage')}</label>
            <select
              id="defaultLanguage"
              value={defaultLanguage}
              onChange={(e) => setDefaultLanguage(e.target.value)}
            >
              <option value="en">English</option>
              <option value="cs">Cestina</option>
            </select>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={allowSelfRegistration}
                onChange={(e) => setAllowSelfRegistration(e.target.checked)}
              />
              {t('superAdmin.organizations.allowSelfRegistration')}
            </label>
          </div>

          <Button type="submit" fullWidth loading={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </form>
      )}

      {activeTab === 'users' && (
        <div className="tab-content">
          {users.length === 0 ? (
            <p>{t('superAdmin.users.noUsers')}</p>
          ) : (
            <DataTable
              data={users}
              columns={userColumns}
              editLink={(user) => `/super-admin/users/${user._id}`}
            />
          )}
        </div>
      )}

      {activeTab === 'subscription' && (
        <div className="tab-content subscription-info">
          {/* Entitlements editor (operator-granted capacity) */}
          <h3>{t('superAdmin.entitlements.title')}</h3>
          <p className="form-help">{t('superAdmin.entitlements.help')}</p>

          <div className="form-group">
            <label htmlFor="memberLimit">{t('superAdmin.entitlements.memberLimit')}</label>
            <NumericInput
              id="memberLimit"
              min={100}
              step={100}
              value={memberLimit}
              onChange={(e) => setMemberLimit(e.target.value)}
            />
            <small className="form-help">{t('superAdmin.entitlements.memberLimitHelp')}</small>
          </div>

          <div className="form-group">
            <label htmlFor="activityLimit">{t('superAdmin.entitlements.activityLimit')}</label>
            <NumericInput
              id="activityLimit"
              min={3}
              step={1}
              value={activityLimit}
              onChange={(e) => setActivityLimit(e.target.value)}
            />
            <small className="form-help">{t('superAdmin.entitlements.activityLimitHelp')}</small>
          </div>

          <div className="form-group">
            <label htmlFor="storageMB">{t('superAdmin.entitlements.storageMB')}</label>
            <NumericInput
              id="storageMB"
              min={0}
              step={100}
              value={storageMB}
              onChange={(e) => setStorageMB(e.target.value)}
            />
          </div>

          <Button onClick={saveEntitlements} loading={savingEntitlements}>
            {savingEntitlements ? t('common.saving') : t('superAdmin.entitlements.save')}
          </Button>

          {organization.subscription && (
            <>
              {organization.subscription.currentPeriodStart && (
                <div className="info-row">
                  <span className="info-label">{t('superAdmin.subscription.periodStart')}</span>
                  <span>{new Date(organization.subscription.currentPeriodStart).toLocaleDateString()}</span>
                </div>
              )}
              {organization.subscription.currentPeriodEnd && (
                <div className="info-row">
                  <span className="info-label">{t('superAdmin.subscription.periodEnd')}</span>
                  <span>{new Date(organization.subscription.currentPeriodEnd).toLocaleDateString()}</span>
                </div>
              )}
              {organization.subscription.expiresAt && (
                <div className="info-row">
                  <span className="info-label">{t('superAdmin.subscription.periodEnd')}</span>
                  <span>{new Date(organization.subscription.expiresAt).toLocaleDateString()}</span>
                </div>
              )}
              {organization.subscription.cancelAtPeriodEnd && (
                <div className="warning-banner">
                  {t('superAdmin.subscription.cancelledWarning')}
                </div>
              )}

              {/* Stripe IDs */}
              {(organization.subscription.stripeCustomerId || organization.subscription.stripeSubscriptionId) ? (
                <>
                  {organization.subscription.stripeCustomerId && (
                    <div className="info-row">
                      <span className="info-label">{t('superAdmin.subscription.stripeCustomerId')}</span>
                      <span><code>{organization.subscription.stripeCustomerId}</code></span>
                    </div>
                  )}
                  {organization.subscription.stripeSubscriptionId && (
                    <div className="info-row">
                      <span className="info-label">{t('superAdmin.subscription.stripeSubscriptionId')}</span>
                      <span><code>{organization.subscription.stripeSubscriptionId}</code></span>
                    </div>
                  )}
                </>
              ) : (
                <div className="info-row">
                  <span className="info-label">Stripe</span>
                  <span style={{ color: '#999' }}>{t('superAdmin.subscription.noStripeIds')}</span>
                </div>
              )}
            </>
          )}

          <div className="subscription-actions">
            <button
              className="action-button"
              onClick={async () => {
                const daysStr = prompt(t('superAdmin.subscription.extendDays'), '30');
                if (!daysStr) return;
                const days = parseInt(daysStr, 10);
                if (isNaN(days) || days <= 0) return;

                try {
                  const currentExpiry = organization.subscription?.expiresAt
                    || organization.subscription?.currentPeriodEnd
                    || new Date().toISOString();
                  const newDate = new Date(currentExpiry);
                  newDate.setDate(newDate.getDate() + days);

                  await api.superAdmin.organizations.update(id!, {
                    name: organization.name,
                    slug: organization.slug,
                    subscription: { expiresAt: newDate.toISOString() },
                  });
                  alert(t('superAdmin.subscription.extendSuccess'));
                  fetchData();
                } catch (err: any) {
                  setError(translateError(err, t));
                }
              }}
            >
              {t('superAdmin.subscription.extend')}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'billing' && (
        <div className="tab-content">
          {billingError && <p className="error-message">{t('common.error')}: {billingError}</p>}

          {billingLoading && !billingLoaded ? (
            <p>{t('common.loading')}</p>
          ) : (
            <>
              {/* 0. Complimentary switch — decides whether anything below applies. */}
              <div className="billing-section">
                <ToggleSwitch
                  id="billing-exempt"
                  checked={!!billingPeriods?.exempt}
                  onChange={toggleExempt}
                  disabled={savingExempt || !billingPeriods}
                  label={t('superAdmin.billing.exemptLabel')}
                />
                <p className="billing-section-note" style={{ marginTop: '0.5rem' }}>
                  {t('superAdmin.billing.exemptHelp')}
                </p>
              </div>

              {/* 1. One record per ended month — what was (or will be) invoiced. */}
              <div className="billing-section">
                <h3>{t('superAdmin.billing.periodsTitle')}</h3>
                <p className="billing-section-note">{t('superAdmin.billing.periodsHelp')}</p>
                {billingPeriods && !billingPeriods.hasBillingDetails && (
                  <p className="billing-section-note">
                    {t('superAdmin.billing.issueNeedsDetails', { fields: billingPeriods.missingBillingFields.join(', ') })}
                  </p>
                )}
                {!billingPeriods || billingPeriods.periods.length === 0 ? (
                  <p className="billing-section-note">{t('superAdmin.billing.noPeriods')}</p>
                ) : (
                  <div className="billing-invoice-table">
                    <DataTable data={billingPeriods.periods} columns={periodColumns} />
                  </div>
                )}
              </div>

              {/* 2. Every invoice ever created, including cancelled ones. */}
              <div className="billing-section">
                <h3>{t('superAdmin.billing.invoicesTitle')}</h3>
                {invoices.length === 0 ? (
                  <p className="billing-section-note">{t('superAdmin.billing.noInvoices')}</p>
                ) : (
                  <div className="billing-invoice-table">
                    <DataTable data={invoices} columns={invoiceColumns} />
                  </div>
                )}
              </div>

              {/* 3. The running month — an estimate, never an invoice amount. */}
              <div className="billing-section">
                <h3>{t('superAdmin.billing.previewTitle')}</h3>
                {billingPreview?.exempt ? (
                  <p className="billing-section-note">{t('superAdmin.billing.previewExempt')}</p>
                ) : billingPreview ? (
                  <>
                    <p className="billing-section-note">{t('superAdmin.billing.estimateNote')}</p>
                    <div className="billing-preview-grid">
                      <div className="billing-preview-item">
                        <span className="billing-label">{t('superAdmin.billing.period')}</span>
                        <span className="billing-preview-value">
                          {formatDate(billingPreview.periodStart)} – {formatDate(billingPreview.periodEnd)}
                        </span>
                      </div>
                      <div className="billing-preview-item">
                        <span className="billing-label">{t('superAdmin.billing.peakMembers')}</span>
                        <span className="billing-preview-value">{Number(billingPreview.peak.members)}</span>
                      </div>
                      <div className="billing-preview-item">
                        <span className="billing-label">{t('superAdmin.billing.peakActivities')}</span>
                        <span className="billing-preview-value">{Number(billingPreview.peak.activities)}</span>
                      </div>
                      <div className="billing-preview-item">
                        <span className="billing-label">{t('superAdmin.billing.currentMembers')}</span>
                        <span className="billing-preview-value">{Number(billingPreview.current.members)}</span>
                      </div>
                      <div className="billing-preview-item">
                        <span className="billing-label">{t('superAdmin.billing.currentActivities')}</span>
                        <span className="billing-preview-value">{Number(billingPreview.current.activities)}</span>
                      </div>
                    </div>

                    {/* Money reads as a receipt: lines, then an emphasised total. */}
                    <div className="billing-cost-breakdown">
                      <div className="billing-cost-row">
                        <span className="billing-cost-label">{t('superAdmin.billing.netAmount')}</span>
                        <span className="billing-cost-value">
                          {Number(billingPreview.netAmount).toFixed(2)} {billingPreview.currency}
                        </span>
                      </div>
                      {Number(billingPreview.vatRate) > 0 && (
                        <div className="billing-cost-row">
                          <span className="billing-cost-label">
                            {t('superAdmin.billing.vatAmount', { rate: Math.round(Number(billingPreview.vatRate) * 100) })}
                          </span>
                          <span className="billing-cost-value">
                            {Number(billingPreview.vatAmount).toFixed(2)} {billingPreview.currency}
                          </span>
                        </div>
                      )}
                      <div className="billing-cost-row billing-cost-row-total">
                        <span className="billing-cost-label">{t('superAdmin.billing.grossAmount')}</span>
                        <span className="billing-cost-value">
                          {Number(billingPreview.grossAmount).toFixed(2)} {billingPreview.currency}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="billing-section-note">{t('superAdmin.billing.noPreview')}</p>
                )}
              </div>

              {/* 4. Set-once legal details, so they come last. */}
              <div className="billing-section">
                <h3>{t('superAdmin.billing.detailsTitle')}</h3>
                <p className="billing-section-note">{t('superAdmin.billing.detailsHelp')}</p>

                <div className="form-grid">
                  <div className="form-grid--full">
                    <FormField label={t('superAdmin.billing.legalName')} htmlFor="billing-legalName" required>
                      <input
                        type="text"
                        id="billing-legalName"
                        className="form-control"
                        value={legalName}
                        onChange={(e) => setLegalName(e.target.value)}
                      />
                    </FormField>
                  </div>

                <FormField label={t('superAdmin.billing.ico')} htmlFor="billing-ico">
                  <input
                    type="text"
                    id="billing-ico"
                    className="form-control"
                    value={ico}
                    onChange={(e) => setIco(e.target.value)}
                  />
                </FormField>
                <FormField label={t('superAdmin.billing.dic')} htmlFor="billing-dic">
                  <input
                    type="text"
                    id="billing-dic"
                    className="form-control"
                    value={dic}
                    onChange={(e) => setDic(e.target.value)}
                  />
                </FormField>
                <FormField label={t('superAdmin.billing.street')} htmlFor="billing-street">
                  <input
                    type="text"
                    id="billing-street"
                    className="form-control"
                    value={street}
                    onChange={(e) => setStreet(e.target.value)}
                  />
                </FormField>
                <FormField label={t('superAdmin.billing.city')} htmlFor="billing-city">
                  <input
                    type="text"
                    id="billing-city"
                    className="form-control"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </FormField>
                <FormField label={t('superAdmin.billing.zip')} htmlFor="billing-zip">
                  <input
                    type="text"
                    id="billing-zip"
                    className="form-control"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                  />
                </FormField>
                <FormField label={t('superAdmin.billing.country')} htmlFor="billing-country">
                  <input
                    type="text"
                    id="billing-country"
                    className="form-control"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                  />
                </FormField>
                <FormField label={t('superAdmin.billing.billingEmail')} htmlFor="billing-email">
                  <input
                    type="email"
                    id="billing-email"
                    className="form-control"
                    value={billingEmail}
                    onChange={(e) => setBillingEmail(e.target.value)}
                  />
                </FormField>

                  <div className="form-grid--full">
                    <FormField label={t('superAdmin.billing.note')} htmlFor="billing-note">
                      <textarea
                        id="billing-note"
                        className="form-control"
                        rows={3}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                      />
                    </FormField>
                  </div>
                </div>

                <div className="billing-section-head" style={{ marginTop: '1rem', marginBottom: 0 }}>
                  <Button onClick={saveBillingDetails} loading={savingBillingDetails} disabled={!legalName.trim()}>
                    {savingBillingDetails ? t('common.saving') : t('superAdmin.billing.saveDetails')}
                  </Button>
                  {billingDetailsSaved && (
                    <p className="billing-section-note" style={{ margin: 0 }}>
                      {t('superAdmin.billing.detailsSaved')}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={!!markPaidTarget}
        onClose={() => setMarkPaidTarget(null)}
        onConfirm={confirmMarkPaid}
        title={t('superAdmin.billing.markPaidTitle')}
        message={t('superAdmin.billing.markPaidMessage', { number: markPaidTarget?.invoiceNumber || '' })}
        confirmText={t('superAdmin.billing.markPaid')}
        cancelText={t('common.cancel')}
        loading={actionLoading}
      />

      <Modal
        isOpen={!!usageEdit}
        onClose={() => setUsageEdit(null)}
        title={usageEdit
          ? t(usageEdit.period.record ? 'superAdmin.billing.usageModalEdit' : 'superAdmin.billing.usageModalCreate', {
              period: formatMonth(usageEdit.period.periodStart),
            })
          : ''}
      >
        {usageEdit && (
          <>
            <p className="billing-section-note">
              {usageEdit.period.record || usageEdit.period.recordedPeak
                ? t('superAdmin.billing.usageModalHelp')
                : t('superAdmin.billing.usageModalHelpUnrecorded')}
            </p>
            <div className="form-grid">
              <FormField label={t('superAdmin.billing.peakMembers')} htmlFor="usage-members" required>
                <NumericInput
                  id="usage-members"
                  min={0}
                  value={usageEdit.members}
                  onChange={(e) => setUsageEdit({ ...usageEdit, members: e.target.value })}
                />
              </FormField>
              <FormField label={t('superAdmin.billing.peakActivities')} htmlFor="usage-activities" required>
                <NumericInput
                  id="usage-activities"
                  min={0}
                  value={usageEdit.activities}
                  onChange={(e) => setUsageEdit({ ...usageEdit, activities: e.target.value })}
                />
              </FormField>
            </div>
            <div className="billing-modal-actions">
              <Button variant="secondary" onClick={() => setUsageEdit(null)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={saveUsageEdit} loading={actionLoading}>
                {t('common.save')}
              </Button>
            </div>
          </>
        )}
      </Modal>

      <ConfirmModal
        isOpen={!!issueTarget}
        onClose={() => setIssueTarget(null)}
        onConfirm={confirmIssue}
        title={t('superAdmin.billing.issueTitle')}
        message={issueTarget?.record ? (
          <>
            <p>
              {t('superAdmin.billing.issueMessage', {
                period: formatMonth(issueTarget.periodStart),
                amount: formatMoney(issueTarget.record.grossAmount, issueTarget.record.currency),
              })}
            </p>
            <ToggleSwitch
              id="issue-email"
              checked={issueEmail}
              onChange={setIssueEmail}
              label={t('superAdmin.billing.issueSendEmail')}
            />
          </>
        ) : ''}
        confirmText={t('superAdmin.billing.issue')}
        cancelText={t('common.cancel')}
        loading={actionLoading}
      />

      <ConfirmModal
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={confirmCancelInvoice}
        title={cancelTarget?.status === 'draft' ? t('superAdmin.billing.discardDraft') : t('superAdmin.billing.cancelTitle')}
        message={cancelTarget?.status === 'draft'
          ? t('superAdmin.billing.discardMessage', { period: formatMonth(cancelTarget.periodStart) })
          : t('superAdmin.billing.cancelMessage', { number: cancelTarget?.invoiceNumber || '' })}
        confirmText={cancelTarget?.status === 'draft' ? t('superAdmin.billing.discardDraft') : t('superAdmin.billing.cancel')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={actionLoading}
      />

      <Link to="/super-admin/organizations" className="form-link">
        {t('superAdmin.organizations.goBack')}
      </Link>
    </div>
  );
};

export default OrganizationDetailPage;
